import React, { useMemo } from 'react';
import type { GuestSiteEventDesign } from '../../../layout.types';
import { brannanConfig, CustomCssGlobalStyle, useBrannanDesignValues } from '../../LayoutBrannan.theme';
import { FrameInner, FrameOuter } from '../Frames';
import { Page } from '../Page';
import type { PageTopPadding } from '../Page/Page';
import chroma from 'chroma-js';
import { useColorPaletteProvider } from '../../../ColorPaletteProvider/ColorPaletteProvider';
import { BrannanNavigation, DesignLayoutType } from '@graphql/generated-base';
import { getCustomArtUrl, getIsCustomArtEnabled } from '../../../layout.utils';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';
import { addRendition } from '@shared/utils/photoRendition';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';

const { layerBack, layerMiddle } = brannanConfig.applicatorHtmlProps;

// Extracted for unit-testability — keeps the mobile/desktop asset selection and rendition logic in one place.
// `shouldHideArt` already carries the breakpoint decision; the caller owns which predicate that is.
export const getShellCustomArtProps = (eventDesign: GuestSiteEventDesign | undefined, isMobileOrTablet: boolean, shouldHideArt = false) => ({
  // The shell always uses the desktop theme asset (not the mobile one) so the body art matches the desktop layout.
  // PRNT-5076: the `hideDesktopArtOnMobile` service-center flag drops it on mobile instead, leaving art
  // only in the title banner — for couples whose desktop upload reads wrong beside the page content.
  isCustomArtEnabled: shouldHideArt ? false : getIsCustomArtEnabled(eventDesign),
  // On mobile we add rendition=medium to avoid downloading the full-res image.
  customArtUrl: isMobileOrTablet ? addRendition({ url: getCustomArtUrl(eventDesign), renditionSize: 'medium' }) : getCustomArtUrl(eventDesign, DesignLayoutType.brannan),
});

export interface ShellProps {
  eventDesign?: GuestSiteEventDesign;
  noTranslucency?: boolean;
  isAlohaGuestPage?: boolean;
  isCustomPage?: boolean;
  /** 'auto' (default) lets the layout decide; 'hidden' e.g. when a masthead should hug the top of the card. */
  topPadding?: PageTopPadding;
  /**
   * Loading state of the page-level useBrannanDesign() call. Shell never invokes the
   * style applicator itself — exactly one writer may paint the shared applicator
   * elements per page (PRNT-4856) — so the owning page passes its loading state down.
   * Omit for skeleton pages with no writer (e.g., RegistryShell during initial load).
   */
  styleApplicatorLoading?: boolean;
}

export const Shell: React.FC<ShellProps> = ({ eventDesign, isCustomPage, topPadding, styleApplicatorLoading = false, children }) => {
  const { applicatorConfig, customCss, isSmallScreen } = useBrannanDesignValues(eventDesign);
  const { pageColor, pageFontColor, artBackgroundColor } = useColorPaletteProvider();

  const isSinglePage = eventDesign?.websiteLayout.layoutType === DesignLayoutType.brannan && eventDesign?.brannanNavigation === BrannanNavigation.singlePage;

  // For some themes (e.g. Rustic Chic) custom background color is not being applied even if enabled
  // This workaround allows to override the default theme color
  const customBackgroundColorConfig = applicatorConfig?.layerBack.customBackgroundColor;
  const customBackgroundColor =
    customBackgroundColorConfig?.isEnabled && customBackgroundColorConfig?.backgroundColor
      ? chroma(customBackgroundColorConfig?.backgroundColor as Array<number>).hex()
      : undefined;

  const customTextColor =
    customBackgroundColorConfig?.isEnabled && customBackgroundColorConfig?.textColor ? chroma(customBackgroundColorConfig?.textColor as Array<number>).hex() : undefined;

  const isMobileOrTablet = useIsMobileOrTablet();
  const [shouldHideDesktopArtOnMobile, isFlagLoading] = useIsFeatureEnabled({ eventFeatureFlagKey: 'hideDesktopArtOnMobile' });
  // Hide on Brannan's own sm4 (960px) breakpoint, not useIsMobileOrTablet (<1024px): EventNameNav
  // renders the title-banner art below sm4 too, so the wider predicate blanks the art entirely
  // between 960 and 1024 instead of leaving it in the title banner.
  // Also hold the art while the flag is unresolved — a flagged event would otherwise paint the
  // desktop art and yank it a tick later. CustomArt is position:fixed, so holding it shifts no layout.
  const shouldHideArt = isSmallScreen && (shouldHideDesktopArtOnMobile || isFlagLoading);
  const { isCustomArtEnabled, customArtUrl } = useMemo(() => getShellCustomArtProps(eventDesign, isMobileOrTablet, shouldHideArt), [eventDesign, isMobileOrTablet, shouldHideArt]);

  return (
    <FrameOuter
      loading={styleApplicatorLoading}
      applicatorProp={layerBack.prop}
      customBackgroundColor={customBackgroundColor}
      loadingBackgroundColor={artBackgroundColor}
      customTextColor={customTextColor}
      isCustomPage={isCustomPage}
      customArtUrl={customArtUrl}
      isCustomArtEnabled={isCustomArtEnabled}
    >
      {customCss && <CustomCssGlobalStyle $customCss={customCss} />}
      <FrameInner>
        <Page
          customBackgroundColor={pageColor}
          customTextColor={pageFontColor}
          applicatorProp={layerMiddle.prop}
          showBorder={applicatorConfig?.layerMiddle.border?.isEnabled}
          noTranslucency={!applicatorConfig?.layerMiddle.backgroundTranslucency?.isEnabled}
          isSinglePage={isSinglePage}
          topPadding={topPadding}
        >
          {children}
        </Page>
      </FrameInner>
    </FrameOuter>
  );
};
